import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Filter, Search } from 'lucide-react';
import { Button } from 'src/features/tyre-management/components/ui/button';
import { Input } from 'src/features/tyre-management/components/ui/input';
import { Label } from 'src/features/tyre-management/components/ui/label';
import SelectControl from 'src/features/tyre-management/components/SelectControl';
import {
  Popover, PopoverContent, PopoverTrigger,
} from 'src/features/tyre-management/components/ui/popover';
import { countAppliedFilters } from '../hooks/vehicleListQuery';

const triState = (value) => {
  if (value === true) return 'true';
  if (value === false) return 'false';
  return '';
};

const fromTriState = (value) => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
};

const QueryWorkbench = ({
  query,
  onSearch,
  onApplyFilters,
  siteOptions,
  groupOptions,
  globalEnabled,
  disabled,
}) => {
  const [draftSearch, setDraftSearch] = useState(query.search);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({
    site: query.site,
    group: query.group,
    active: query.active,
    tyre_management_enabled: globalEnabled ? query.tyre_management_enabled : null,
  });
  const appliedCount = countAppliedFilters(globalEnabled
    ? query
    : { ...query, tyre_management_enabled: null });

  useEffect(() => {
    setDraftSearch(query.search);
  }, [query.search]);

  useEffect(() => {
    const handle = setTimeout(() => {
      if (draftSearch !== query.search) onSearch(draftSearch);
    }, 300);
    return () => clearTimeout(handle);
  }, [draftSearch, onSearch, query.search]);

  const openFilters = (nextOpen) => {
    if (nextOpen) {
      setDraft({
        site: query.site,
        group: query.group,
        active: query.active,
        tyre_management_enabled: globalEnabled ? query.tyre_management_enabled : null,
      });
    }
    setOpen(nextOpen);
  };

  const apply = () => {
    onApplyFilters(globalEnabled ? draft : { ...draft, tyre_management_enabled: null });
    setOpen(false);
  };

  const siteChoices = useMemo(() => [{ id: '', name: 'All sites' }, ...siteOptions], [siteOptions]);
  const groupChoices = useMemo(() => [{ id: '', name: 'All groups' }, ...groupOptions], [groupOptions]);

  return (
    <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center lg:w-auto">
      <div className="relative min-w-[260px] flex-1">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
        <Input
          value={draftSearch}
          onChange={(event) => setDraftSearch(event.target.value)}
          className="pl-9"
          placeholder="Find by registration, model, or asset code"
          aria-label="Find by registration, model, or asset code"
          disabled={disabled}
        />
      </div>
      <Popover open={open} onOpenChange={openFilters}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" className="h-10 shrink-0" disabled={disabled}>
            <Filter />
            {appliedCount ? `Filters ${appliedCount}` : 'Filters'}
          </Button>
        </PopoverTrigger>
        <PopoverContent aria-label="Vehicle filters">
          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="vl-filter-site">Site</Label>
              <SelectControl
                id="vl-filter-site"
                value={draft.site}
                onChange={(event) => setDraft((current) => ({ ...current, site: event.target.value }))}
                searchable
                searchPlaceholder="Search sites…"
              >
                {siteChoices.map((item) => (
                  <option key={item.id || 'all'} value={item.id}>{item.name}</option>
                ))}
              </SelectControl>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="vl-filter-group">Group</Label>
              <SelectControl
                id="vl-filter-group"
                value={draft.group}
                onChange={(event) => setDraft((current) => ({ ...current, group: event.target.value }))}
                searchable
                searchPlaceholder="Search groups…"
              >
                {groupChoices.map((item) => (
                  <option key={item.id || 'all'} value={item.id}>{item.name}</option>
                ))}
              </SelectControl>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="vl-filter-active">Active</Label>
              <SelectControl
                id="vl-filter-active"
                value={triState(draft.active)}
                onChange={(event) => setDraft((current) => ({ ...current, active: fromTriState(event.target.value) }))}
              >
                <option value="">Any</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </SelectControl>
            </div>
            {globalEnabled && <div className="grid gap-1.5">
              <Label htmlFor="vl-filter-opt-in">Tyre tracking</Label>
              <SelectControl
                id="vl-filter-opt-in"
                value={triState(draft.tyre_management_enabled)}
                onChange={(event) => setDraft((current) => ({
                  ...current,
                  tyre_management_enabled: fromTriState(event.target.value),
                }))}
              >
                <option value="">Any</option>
                <option value="true">On</option>
                <option value="false">Off</option>
              </SelectControl>
            </div>}
            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setDraft({
                  site: '', group: '', active: null, tyre_management_enabled: null,
                })}
              >
                Clear
              </Button>
              <Button type="button" size="sm" onClick={apply}>Apply</Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

QueryWorkbench.propTypes = {
  query: PropTypes.shape({
    search: PropTypes.string,
    site: PropTypes.string,
    group: PropTypes.string,
    active: PropTypes.bool,
    tyre_management_enabled: PropTypes.bool,
  }).isRequired,
  onSearch: PropTypes.func.isRequired,
  onApplyFilters: PropTypes.func.isRequired,
  siteOptions: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), name: PropTypes.string })),
  groupOptions: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), name: PropTypes.string })),
  globalEnabled: PropTypes.bool,
  disabled: PropTypes.bool,
};

QueryWorkbench.defaultProps = {
  siteOptions: [],
  groupOptions: [],
  globalEnabled: false,
  disabled: false,
};

export default QueryWorkbench;
